import React, { useState, useEffect } from 'react';
import { Routes, Route, Navigate, useNavigate } from 'react-router-dom';
import StackedLayout from './components/StackedLayout';
import LoginPage from './pages/LoginPage';
import RegisterPage from './pages/RegisterPage';
import Summary from './pages/Summary';
import Profile from './pages/Profile';
import Latest from './pages/Latest';
import Upload from './pages/Upload';
import History from './pages/History';
import Chat from './pages/Chat';
import MixMatch from './pages/MixMatch';
import KnowledgeBase from './pages/KnowledgeBase';
import Terms from './pages/Terms';
import Privacy from './pages/Privacy';
import { supabase } from './supabaseClient';
import UserDropdown from './components/UserDropdown';
import ThemeToggle from './components/ThemeToggle';
import Agent from './pages/Agent';
import Task from './pages/Task';
import Favourite from './pages/Favourite';
import Collections from './pages/Collections';
import Analytics from './pages/Analytics';
import Trends from './pages/Trends';
import Recommendations from './pages/Recommendations';
import Planning from './pages/Planning';
import Reports from './pages/Reports';
import Styling from './pages/Styling';
import FabricIntelligence from './pages/FabricIntelligence';
import ConceptualCuration from './pages/ConceptualCuration';
import IndustryResearch from './components/IndustryResearch';
import Videos from './pages/Videos';
import UIShowcase from './pages/UIShowcase';
import HeroPage from './pages/HeroPage';
import Products from './pages/Products';
import Brands from './pages/Brands';
import Customers from './pages/Customers';
import News from './pages/News';
import { API_BASE_URL } from './utils/api';

function App() {
  const [session, setSession] = useState(null);
  const [authLoading, setAuthLoading] = useState(true);
  const [accountName, setAccountName] = useState('');
  const [userProfile, setUserProfile] = useState(null);
  const navigate = useNavigate();

  const fetchProfileAndSetState = async (token) => {
    try {
      const res = await fetch(`${API_BASE_URL}/api/profile`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) {
        const profile = await res.json();
        setUserProfile(profile);
        if (profile.name) setAccountName(profile.name);
      }
    } catch (err) {
      // Optionally handle error
    }
  };

  useEffect(() => {
    supabase.auth.getSession().then(({ data }) => {
      setSession(data.session);
      if (data.session?.user?.user_metadata?.account_name) {
        setAccountName(data.session.user.user_metadata.account_name);
      }
      if (data.session?.access_token) {
        fetchProfileAndSetState(data.session.access_token);
      }
      setAuthLoading(false);
    });
    let listener = null;
    try {
      const listenerResp = supabase.auth.onAuthStateChange((_event, session) => {
        setSession(session);
        if (session?.user?.user_metadata?.account_name) {
          setAccountName(session.user.user_metadata.account_name);
        }
        if (session?.access_token) {
          fetchProfileAndSetState(session.access_token);
        }
      });
      listener = listenerResp?.data?.listener || listenerResp?.data?.subscription || listenerResp?.subscription;
    } catch (err) {
      setAuthLoading(false);
    }
    return () => {
      try {
        if (listener?.unsubscribe) listener.unsubscribe();
        else if (listener?.subscription?.unsubscribe) listener.subscription.unsubscribe();
      } catch (err) {
        // no-op
      }
    };
  }, []);

  if (authLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 text-gray-500">
        Loading...
      </div>
    );
  }

  const handleAuthSuccess = (session) => {
    setSession(session);
    navigate('/dashboard');
    if (session?.access_token) {
      fetchProfileAndSetState(session.access_token);
    }
  };

  const updateUserMetadata = (newMetadata) => {
    setSession((prevSession) => {
      if (!prevSession) return prevSession;
      return {
        ...prevSession,
        user: {
          ...prevSession.user,
          user_metadata: {
            ...prevSession.user.user_metadata,
            ...newMetadata,
          },
        },
      };
    });
    setUserProfile((prev) => ({ ...prev, ...newMetadata }));
    if (newMetadata.account_name) {
      setAccountName(newMetadata.account_name);
    }
  };

  if (!session?.user) {
    return (
      <Routes>
        <Route path="/" element={<HeroPage />} />
        <Route path="/login" element={<LoginPage onAuthSuccess={handleAuthSuccess} />} />
        <Route path="/register" element={<RegisterPage onAuthSuccess={handleAuthSuccess} />} />
        <Route path="/terms" element={<Terms />} />
        <Route path="/privacy" element={<Privacy />} />
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    );
  }

  const handleLogout = async () => {
    const { error } = await supabase.auth.signOut();
    setSession(null);
    setAccountName('');
    navigate('/login');
    if (error && process.env.NODE_ENV !== 'production') {
      // eslint-disable-next-line no-console
      console.error('Logout error:', error.message);
    }
  };

  return (
    <StackedLayout
      user={{
        name: userProfile?.name || accountName || (session?.user?.email?.split('@')[0] || 'User'),
        email: session?.user?.email,
        avatar_url: userProfile?.avatar_url || session?.user?.user_metadata?.avatar_url,
        company: userProfile?.company
      }}
      onLogout={handleLogout}
      themeToggle={<ThemeToggle />}
    >
      <Routes>
        <Route path="/dashboard" element={<Task />} />
        <Route path="/summary" element={<Summary />} />
        <Route path="/profile" element={<Profile updateUserMetadata={updateUserMetadata} />} />
        <Route path="/latest" element={<Latest />} />
        <Route path="/collections" element={<Collections />} />
        <Route path="/products" element={<Products />} />
        <Route path="/brands" element={<Brands />} />
        <Route path="/customers" element={<Customers />} />
        <Route path="/news" element={<News />} />
        <Route path="/analytics" element={<Analytics />} />
        <Route path="/trends" element={<Trends />} />
        <Route path="/recommendations" element={<Recommendations />} />
        <Route path="/planning" element={<Planning />} />
        <Route path="/reports" element={<Reports />} />
        <Route path="/styling" element={<Styling />} />
        <Route path="/fabric-intelligence" element={<FabricIntelligence />} />
        <Route path="/conceptual-curation" element={<ConceptualCuration />} />
        <Route path="/industry-research" element={<IndustryResearch />} />
        <Route path="/videos" element={<Videos />} />
        <Route path="/ui-showcase" element={<UIShowcase />} />
        <Route path="/favourite" element={<Favourite />} />
        <Route path="/upload" element={<Upload buyerId={session?.user?.id || ''} />} />
        <Route path="/history" element={<History buyerId={session?.user?.id || ''} />} />
        <Route path="/chat" element={<Chat userId={session?.user?.id || ''} />} />
        <Route path="/mixmatch" element={<MixMatch />} />
        <Route path="/knowledge-base" element={<KnowledgeBase />} />
        <Route path="/agent" element={<Agent />} />
        <Route path="/task" element={<Navigate to="/dashboard" replace />} />
        <Route path="*" element={<Navigate to="/dashboard" replace />} />
      </Routes>
    </StackedLayout>
  );
}

export default App;
